import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, Star, ShieldCheck, MessageCircle, Globe, Cloud, ArrowRight } from 'lucide-react';

const CaseStudy = () => {
  const results = [
    {
      icon: ShieldCheck,
      stat: "Google Guaranteed",
      label: "Status Achieved",
      color: "text-green-500",
      bgColor: "bg-green-50",
    },
    {
      icon: Clock,
      stat: "15 hrs/week",
      label: "Admin Time Saved",
      color: "text-blue-500",
      bgColor: "bg-blue-50",
    },
    {
      icon: Star,
      stat: "150+",
      label: "5-Star Reviews",
      color: "text-yellow-500",
      bgColor: "bg-yellow-50",
    },
  ];

  const solutions = [
    {
      icon: Globe,
      title: "Digital Front Door",
      description: "We built a high-performance website (happyvan.co) integrated with Google Local Services Ads, earning them the coveted \"Google Guaranteed\" green tick.",
    },
    {
      icon: MessageCircle,
      title: "Smart Automation",
      description: "We implemented WhatsApp for Business to enable \"Video Quotes\"—allowing customers to walk around their house filming items, saving days of in-person survey time.",
    },
    {
      icon: Cloud,
      title: "The Connected Workspace",
      description: "We migrated their operations to Google Workspace, ensuring every driver has secure access to job sheets and client details on the road, 100% paper-free.",
    },
  ];

  return (
    <section className="py-20 md:py-32 bg-[var(--text-primary)] overflow-hidden">
      <div className="container">
        <div className="max-w-6xl mx-auto">
          {/* Section Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[var(--accent-primary)] text-[var(--text-primary)] text-sm font-semibold mb-6">
              <Star className="w-4 h-4" />
              Featured Success Story
            </span>
          </motion.div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left - Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              {/* Client Logo/Name */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center text-white font-bold text-xl">
                  H
                </div>
                <div>
                  <p className="text-white font-semibold">HappyVan</p>
                  <p className="text-white/50 text-sm">Removals & Logistics</p>
                </div>
              </div>

              {/* Headline */}
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                From "Man & Van" to Digital Logistics Brand
              </h2>
              <p className="text-white/70 text-lg mb-8">
                How we helped a local removals firm automate quotes and secure 150+ 5-star reviews.
              </p>

              {/* The Challenge */}
              <div className="mb-8 p-6 rounded-xl bg-white/5 border border-white/10">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 text-sm">!</span>
                  The Challenge
                </h3>
                <p className="text-white/70 leading-relaxed">
                  HappyVan was a successful local business relying on phone calls and manual diaries. They needed to scale up from a "man with a van" image to a professional logistics brand without hiring more admin staff.
                </p>
              </div>

              {/* The Solution */}
              <div className="space-y-4 mb-8">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[var(--accent-primary)] flex items-center justify-center text-[var(--text-primary)] text-sm">✓</span>
                  The Solution (Our Tier 2 Package)
                </h3>
                {solutions.map((solution, index) => (
                  <motion.div
                    key={solution.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex gap-4"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[var(--accent-primary)]/20 flex items-center justify-center flex-shrink-0">
                      <solution.icon className="w-5 h-5 text-[var(--accent-primary)]" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium text-sm mb-1">{solution.title}</h4>
                      <p className="text-white/60 text-sm leading-relaxed">{solution.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* CTA */}
              <a
                href="#contact"
                className="inline-flex items-center gap-2 btn-primary"
              >
                Get Similar Results
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>

            {/* Right - Phone Mockup & Results */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              {/* Results Cards - Floating */}
              <div className="absolute -top-4 -left-4 md:left-0 z-20">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="bg-white rounded-xl shadow-xl p-4 flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="font-bold text-[var(--text-primary)]">Google Guaranteed</p>
                    <p className="text-xs text-[var(--text-muted)]">Verified Business</p>
                  </div>
                </motion.div>
              </div>

              {/* Phone Mockup */}
              <div className="relative mx-auto w-[280px] md:w-[320px]">
                {/* Phone Frame */}
                <div className="relative bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-gray-900 rounded-b-2xl z-10" />
                  
                  {/* Screen */}
                  <div className="relative bg-white rounded-[2.5rem] overflow-hidden">
                    {/* Status Bar */}
                    <div className="bg-orange-500 px-6 py-3 flex items-center justify-between">
                      <span className="text-white text-xs font-medium">9:41</span>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-2 bg-white/80 rounded-sm" />
                        <div className="w-6 h-3 bg-white rounded-sm" />
                      </div>
                    </div>

                    {/* Website Content */}
                    <div className="bg-gradient-to-b from-orange-500 to-orange-600 px-4 py-6 text-center">
                      <div className="w-16 h-16 rounded-full bg-white mx-auto mb-3 flex items-center justify-center">
                        <span className="text-orange-500 font-bold text-2xl">H</span>
                      </div>
                      <h3 className="text-white font-bold text-lg mb-1">HappyVan</h3>
                      <p className="text-white/80 text-xs mb-2">Professional Removals & Logistics</p>
                      <div className="flex items-center justify-center gap-1 mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-yellow-300 fill-yellow-300" />
                        ))}
                        <span className="text-white/80 text-xs ml-1">(150+)</span>
                      </div>
                      
                      {/* Google Guaranteed Badge */}
                      <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1.5 mb-4">
                        <ShieldCheck className="w-4 h-4 text-white" />
                        <span className="text-white text-xs font-medium">Google Guaranteed</span>
                      </div>
                    </div>

                    {/* Content Area */}
                    <div className="bg-white px-4 py-4">
                      <div className="bg-gray-50 rounded-xl p-4 mb-4">
                        <p className="text-gray-600 text-xs text-center mb-3">Get an instant quote via video!</p>
                        <div className="flex gap-2">
                          <button className="flex-1 bg-orange-500 text-white text-xs font-medium py-2.5 rounded-lg">
                            Get Quote
                          </button>
                          <button className="flex-1 bg-gray-200 text-gray-700 text-xs font-medium py-2.5 rounded-lg">
                            Call Us
                          </button>
                        </div>
                      </div>

                      {/* Services preview */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          <span>House Removals</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          <span>Office Relocations</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          <span>Storage Solutions</span>
                        </div>
                      </div>
                    </div>

                    {/* Home Indicator */}
                    <div className="bg-white pb-2 pt-1">
                      <div className="w-32 h-1 bg-gray-300 rounded-full mx-auto" />
                    </div>
                  </div>
                </div>

                {/* WhatsApp Button - Floating */}
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: 0.6 }}
                  className="absolute -right-4 bottom-24 z-20"
                >
                  <div className="w-14 h-14 rounded-full bg-[#25D366] shadow-lg flex items-center justify-center">
                    <MessageCircle className="w-7 h-7 text-white" />
                  </div>
                  {/* Ping animation */}
                  <span className="absolute top-0 right-0 w-4 h-4">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#25D366] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-4 w-4 bg-[#25D366]"></span>
                  </span>
                </motion.div>

                {/* Chat bubble */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.8 }}
                  className="absolute -right-2 md:right-[-80px] bottom-40 z-20"
                >
                  <div className="bg-white rounded-2xl rounded-br-sm shadow-xl p-3 max-w-[180px]">
                    <p className="text-xs text-gray-700">
                      📹 Send us a video of your items for an instant quote!
                    </p>
                  </div>
                </motion.div>
              </div>

              {/* Results Grid - Below Phone */}
              <div className="grid grid-cols-3 gap-3 mt-8">
                {results.map((result, index) => (
                  <motion.div
                    key={result.label}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/10"
                  >
                    <div className={`w-10 h-10 rounded-lg ${result.bgColor} flex items-center justify-center mx-auto mb-2`}>
                      <result.icon className={`w-5 h-5 ${result.color}`} />
                    </div>
                    <p className="text-white font-bold text-sm">{result.stat}</p>
                    <p className="text-white/50 text-xs">{result.label}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudy;
